import { Role } from '../../utils/roles'

export const AUTHORIZATION_ROLES = [
  Role.ADMIN,
  Role.SUPER_ADMIN,
  Role.PARTNER_ADMIN,
  Role.PARTNER_OWNER,
  Role.PARTNER_STAFF,
] as const

export type AuthorizationRole = (typeof AUTHORIZATION_ROLES)[number]

/**
 * JWT scope strings (cloud:service:operation).
 * Operation may be legacy `read` | `write` or CRUD `view` | `create` | `update` | `delete`.
 *
 * - Canvas / Salesforce: mapped in backend `scopeMapping.ts` from permission sets (read/write).
 * - Auth0: identity service permissions are injected as claims; platform may include
 *   `all:labra:write` for full product access.
 */
const marketplaceScopes = (
  cloud: 'aws' | 'azure' | 'gcp'
): readonly string[] => [
  `${cloud}:marketplace:read`,
  `${cloud}:marketplace:write`,
  `${cloud}:marketplace:view`,
  `${cloud}:marketplace:create`,
  `${cloud}:marketplace:update`,
  `${cloud}:marketplace:delete`,
]

const coSellScopes = (cloud: 'aws' | 'azure' | 'gcp'): readonly string[] => [
  `${cloud}:cosell:read`,
  `${cloud}:cosell:write`,
  `${cloud}:cosell:view`,
  `${cloud}:cosell:create`,
  `${cloud}:cosell:update`,
  `${cloud}:cosell:delete`,
]

export const APP_SCOPES = [
  'all:labra:write',
  ...marketplaceScopes('aws'),
  ...marketplaceScopes('azure'),
  ...marketplaceScopes('gcp'),
  ...coSellScopes('aws'),
  ...coSellScopes('azure'),
  ...coSellScopes('gcp'),
  'all:propensity:read',
  'all:propensity:write',
  'all:propensity:view',
  'all:propensity:create',
  'all:propensity:update',
  'all:propensity:delete',
] as const

export type AppScope = (typeof APP_SCOPES)[number]

const AUTH_ROLE_SET = new Set<string>(AUTHORIZATION_ROLES)
const APP_SCOPE_SET = new Set<string>(APP_SCOPES)

export const isAuthorizationRole = (
  value: string
): value is AuthorizationRole => AUTH_ROLE_SET.has(value)

export const isAppScope = (value: string): value is AppScope =>
  APP_SCOPE_SET.has(value)

/** Labra product surfaces used in authz checks (matches scope middle segment). */
export type LabraService = 'marketplace' | 'cosell' | 'propensity'

/** Cloud segment in scope strings (lowercase). */
export type AuthorizationCloud = 'aws' | 'azure' | 'gcp' | 'all'

/** Resource action for policy checks (OPA `resource.action`). */
export type AccessLevel = 'view' | 'create' | 'update' | 'delete'
